import * as React from "react";
import { memo } from "react";
import {
  CategoryDetailResult,
  CategoryProductCountsMap,
  CategorySummaryResult,
} from "../../../../../microservices/functions/src/types/product_catalog/catalog_api_types";

type CategorySidebarProps = {
  currentCategoryId: number;
  category: CategoryDetailResult;
};

const groupByParentId = (
  categories: CategorySummaryResult[]
): Record<string | "_roots", CategorySummaryResult[]> => {
  const byParentId: Record<string | "_roots", CategorySummaryResult[]> = {};

  categories.forEach((category) => {
    const parentId = category.parentId ?? "_roots";
    if (byParentId[parentId] === undefined) {
      byParentId[parentId] = [];
    }
    byParentId[parentId].push(category);
  });

  return byParentId;
};

export const CategorySidebar = memo(
  ({ currentCategoryId, category }: CategorySidebarProps) => {
    const { ancestorIds, relatedCategories, productCounts } = category;
    const byParentId = groupByParentId(Object.values(relatedCategories));
    const activePathIds = [...ancestorIds, currentCategoryId];

    const getById = (
      categoryId: number | undefined
    ): CategorySummaryResult | undefined => {
      if (categoryId === undefined) {
        return undefined;
      }
      return relatedCategories[categoryId];
    };

    const getChildren: GetChildren = (parentId) => {
      if (parentId === undefined) {
        return undefined;
      }
      if (parentId === null) {
        return byParentId["_roots"];
      }
      return byParentId[parentId]?.sort((a, b) => a.position - b.position);
    };

    const isInActivePath: IsInActivePath = (categoryId) =>
      activePathIds.includes(categoryId);

    const ancestorUnderActiveRoot = getById(ancestorIds[1]);
    const thirdLevelCousins = getChildren(ancestorUnderActiveRoot?.id);

    return (
      <ul className="active-path">
        <li>
          {ancestorUnderActiveRoot && (
            <>
              <strong>
                <a href={ancestorUnderActiveRoot.linkPath}>
                  <span>{ancestorUnderActiveRoot.shortPluralName}</span>
                </a>
              </strong>
              {thirdLevelCousins && (
                <CategoryTreeLevel
                  getChildren={getChildren}
                  isInActivePath={isInActivePath}
                  currentCategoryId={currentCategoryId}
                  categories={thirdLevelCousins}
                  productCounts={productCounts}
                />
              )}
            </>
          )}
        </li>
      </ul>
    );
  }
);

type GetChildren = (
  parentId: number | undefined | null
) => CategorySummaryResult[] | undefined;

type IsInActivePath = (categoryId: number) => boolean;

type CategoryTreeLevelProps = {
  categories: CategorySummaryResult[];
  getChildren: GetChildren;
  isInActivePath: IsInActivePath;
  currentCategoryId: number;
  productCounts: CategoryProductCountsMap;
};

const CategoryTreeLevel = ({
  categories,
  getChildren,
  isInActivePath,
  currentCategoryId,
  productCounts,
}: CategoryTreeLevelProps): JSX.Element => (
  <ul>
    {categories.map((category) => (
      <CategoryTreeItem
        key={category.id}
        category={category}
        getChildren={getChildren}
        isInActivePath={isInActivePath}
        currentCategoryId={currentCategoryId}
        productCounts={productCounts}
      />
    ))}
  </ul>
);

type CategoryTreeItemProps = {
  category: CategorySummaryResult;
  getChildren: GetChildren;
  isInActivePath: IsInActivePath;
  currentCategoryId: number;
  productCounts: CategoryProductCountsMap;
};

const CategoryTreeItem = ({
  category,
  getChildren,
  isInActivePath,
  currentCategoryId,
  productCounts,
}: CategoryTreeItemProps): JSX.Element => {
  const myChildren = getChildren(category.id);
  const thisIsActive = isInActivePath(category.id);

  return (
    <li className={myChildren === undefined ? "leaf" : "child"}>
      <a
        href={category.linkPath}
        className={currentCategoryId === category.id ? "active" : undefined}
      >
        {category.shortPluralName}{" "}
        <small>({productCounts[category.id] ?? 0})</small>
      </a>
      {myChildren !== undefined && thisIsActive && (
        <CategoryTreeLevel
          categories={myChildren}
          getChildren={getChildren}
          isInActivePath={isInActivePath}
          currentCategoryId={currentCategoryId}
          productCounts={productCounts}
        />
      )}
    </li>
  );
};
